import React, { FC } from "react";
import {
  Box,
  Typography,
  Toolbar,
  Card,
  CardContent,
  Grid,
} from "@mui/material";

interface ComponentProps {
  entityTitle?: string;
  entityFields: Array<{
    label: string;
    value: React.ReactNode;
    columnPreference?: 1 | 2;
    multiline?: boolean;
  }>;
  tooltips?: React.ReactNode;
  children?: React.ReactNode;
}

const SingleCardDetails: FC<ComponentProps> = ({
  entityTitle,
  entityFields,
  tooltips,
  children,
}) => {
  const fieldsWithPreference = entityFields.filter(
    (field) => field.columnPreference && !field.multiline
  );
  const fieldsWithoutPreference = entityFields.filter(
    (field) => !field.columnPreference && !field.multiline
  );
  const multilineData = entityFields.filter((field) => field.multiline);

  const sortedFieldsWithPreference = fieldsWithPreference.sort(
    (a, b) => (a.columnPreference || 0) - (b.columnPreference || 0)
  );

  const firstColumnData: any[] = [];
  const secondColumnData: any[] = [];

  sortedFieldsWithPreference.forEach((field) => {
    if (field.columnPreference === 1) {
      firstColumnData.push(field);
    } else if (field.columnPreference === 2) {
      secondColumnData.push(field);
    }
  });

  fieldsWithoutPreference.forEach((field) => {
    if (firstColumnData.length <= secondColumnData.length) {
      firstColumnData.push(field);
    } else {
      secondColumnData.push(field);
    }
  });

  const FieldRow: FC<{ dataItem: any; index: number }> = ({
    dataItem,
    index,
  }) =>
    dataItem?.value && (
      <Box
        key={`field-row-${index}`}
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography
          variant="body2"
          fontWeight="500"
          sx={{
            flexShrink: 0,
            width: "150px",
          }}
        >
          {dataItem?.label}:
        </Typography>
        <Typography
          variant="body2"
          flexGrow={1}
          sx={{
            wordBreak: "break-word",
          }}
        >
          {dataItem?.value}
        </Typography>
      </Box>
    );

  return (
    <>
      <Box>
        {/* Title and Tooltips Section */}
        {(entityTitle || tooltips) && (
          <Toolbar sx={{ justifyContent: "space-between", mb: 2 }}>
            {entityTitle && <Typography variant="h6">{entityTitle}</Typography>}
            {tooltips && <Box sx={{ ml: "auto" }}>{tooltips}</Box>}
          </Toolbar>
        )}

        {/* Main Card for all entity details */}
        {entityFields.length > 0 && (
          <Card
            sx={{
              padding: 3,
              margin: 3,
              backgroundColor: "#f0f3fa",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#f0f3fa",
                transition: "background-color 0.3s ease",
              },
            }}
          >
            <CardContent sx={{ p: 3, ml: 5 }}>
              <Grid container spacing={3}>
                {/* Render first column */}
                <Grid item xs={12} md={6}>
                  {firstColumnData.map((dataItem: any, dataIndex: number) => (
                    <FieldRow
                      key={`first-col-${dataIndex}`}
                      dataItem={dataItem}
                      index={dataIndex}
                    />
                  ))}
                </Grid>

                {/* Render second column */}
                <Grid item xs={12} md={6}>
                  {secondColumnData.map((dataItem: any, dataIndex: number) => (
                    <FieldRow
                      key={`second-col-${dataIndex}`}
                      dataItem={dataItem}
                      index={dataIndex}
                    />
                  ))}
                </Grid>
              </Grid>

              {/* Render multiline data below the columns */}
              {multilineData.length > 0 && (
                <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #E0E0E0" }}>
                  {multilineData.map(
                    (dataItem: any, dataIndex: number) =>
                      dataItem?.value && (
                        <Box
                          key={`multiline-${dataIndex}`}
                          sx={{
                            mb: dataIndex < multilineData.length - 1 ? 2 : 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              flexShrink: 0,
                              width: "100%",
                            }}
                          >
                            {dataItem?.label}:
                          </Typography>
                          <Typography
                            variant="body2"
                            flexGrow={1}
                            sx={{
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {dataItem?.value}
                          </Typography>
                        </Box>
                      )
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Children content area */}
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </>
  );
};

export default SingleCardDetails;
